import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  Grid,
  Image,
  Input,
  Modal,
  Row,
  Select,
  SelectProps,
  Table,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Copy from "../../assets/images/icons/Copy";
import { CustomMessage } from "../../components";
import { P1, P2 } from "../../components/NormalFonts";
import { CancelPolicyType } from "../../hooks/Order/data";
import {
  useGetRefundPolicy,
  useGetUserOrderDetail,
  useOrderRefund,
} from "../../hooks/Order/order.hooks";
import { SearchResponse, SearchSessionDetail } from "../../hooks/Search/data";
import useGetTimezone from "../../hooks/Timezone/timezone.hooks";
import { ContentWrapper } from "../../layouts/components";
import { moneyFormat } from "../../plugins/numberFormat";
import { copyText, envChangeType } from "../../plugins/utils";
import { InputRequired } from "../../validations/validations";
import { CardsEnumType } from "./data";
import { refundCount, refundType } from "./enumerations/refundCount";
import styles from "./styles.module.scss";
import "./styles.scss";

const useQueryParams = () => {
  const { search } = useLocation();
  return Object.fromEntries(new URLSearchParams(search));
};

const Refund = () => {
  const { t } = useTranslation();
  const { useBreakpoint } = Grid;
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const { TextArea } = Input;
  const { confirm } = Modal;
  const [form] = Form.useForm();

  const [calculatedAmount, setCalculatedAmount] = useState<number[]>([]);
  const [totalRefundAmount, setTotalRefundAmount] = useState<number>(0);

  const [queryParams, setQueryParams] = useState<any>(useQueryParams());
  const { formatDate, toLocalTimezone, forceDate } = useGetTimezone();

  const { mutate: refund, isPending: isPendingRefund } = useOrderRefund();

  const { data: orderDetail } = useGetUserOrderDetail(
    { payment_number: queryParams.payment_number },
    { enabled: !!queryParams && !!queryParams.payment_number }
  );

  // const { data: refundData } = useGetRefundData(queryParams, {
  //   enabled:
  //     !!queryParams &&
  //     !!queryParams.payment_number &&
  //     !!queryParams.order_numbers,
  // });

  const { data: refundPolicy } = useGetRefundPolicy({});
  const [tableSource, setTableSource] = useState<CancelPolicyType[]>([]);

  useEffect(() => {
    if (!refundPolicy?.policy?.cancellation_policy) return;
    console.debug("refundPolicy", refundPolicy);
    setTableSource(refundPolicy.policy.cancellation_policy);
  }, [refundPolicy]);

  useEffect(() => {
    if (!orderDetail) return;
    console.debug("orderDetail", orderDetail);
    const countOrders = orderDetail.order_details.length;
    setCalculatedAmount(Array(countOrders).fill(0));
  }, [orderDetail]);

  useEffect(() => {
    setTotalRefundAmount(calculatedAmount.reduce((acc, val) => acc + val, 0));
  }, [calculatedAmount]);

  const onCancel = () => {
    navigate(-1);
  };

  const convertToArray = (input: any) => {
    const result: any = {
      refund_orders_info: [],
    };

    let index = 0;
    while (`refund_type${index}` in input) {
      switch (input[`refund_type${index}`]) {
        case refundType.REFUND_ALL_TYPE:
          result.refund_orders_info.push({
            order_number: input[`order_number${index}`],
            refund_type: input[`refund_type${index}`],
          });
          break;
        case refundType.PARTIAL_REFUND_TYPE:
          result.refund_orders_info.push({
            order_number: input[`order_number${index}`],
            refund_type: input[`refund_type${index}`],
            refund_person_count: input[`refund_person_count${index}`],
          });
          break;
        default:
          break;
      }
      index++;
    }

    return result;
  };

  const onFinish = (value: any) => {
    const request = {
      refund_reason: value.refund_reason,
      payment_number: queryParams.payment_number,
      refund_amount: Math.trunc(totalRefundAmount),
      ...convertToArray(value),
    };
    confirm({
      title: t("record.applyRefund"),
      content: (
        <div>
          <Trans i18nKey={"refund.confirm.content"} />
        </div>
      ),
      icon: null,
      okButtonProps: { style: { backgroundColor: "var(--error)" } },
      okText: t("normal.confirm"),
      cancelText: t("normal.cancel"),
      onOk: () => {
        refund(
          {
            params: request,
          },
          {
            onSuccess: (success: any) => {
              CustomMessage({ alertMessage: t("normal.submit.success") });
              setTimeout(() => navigate(-1), 1000);
            },
            onError: (error: any) => {
              CustomMessage({
                alertMessage: t([
                  `error.${error.data.result.code}`,
                  error.data.result.message,
                ]),
                alertType: "error",
              });
            },
          }
        );
      },
      onCancel: () => {},
    });
  };

  const tableColumn = [
    {
      title: t("refund.deadline"),
      dataIndex: "range",
      key: "range",
      align: "center" as "center",
      render: (text: string, record: CancelPolicyType) => (
        <P2
          color={`${record.refundable === true ? "#1C222B" : "var(--error)"}`}
        >
          {t(`refund.policy.${record.key}`)}
        </P2>
      ),
    },
    {
      title: t("refund.amount"),
      dataIndex: "fee_percent",
      key: "fee_percent",
      align: "center" as "center",
      render: (text: string, record: CancelPolicyType) => (
        <P2
          color={`${record.refundable === true ? "#1C222B" : "var(--error)"}`}
        >
          {record.refundable === true ? `${text}%` : t("refund.nonrefundable")}
        </P2>
      ),
    },
  ];

  const renderDate = (sessions: SearchSessionDetail[]) => {
    if (sessions.length <= 0) return;
    if (sessions.length <= 1) {
      return formatDate(toLocalTimezone(sessions[0].date));
    }
    return `${formatDate(
      toLocalTimezone(sessions[0].date)
    )} - ${toLocalTimezone(sessions[sessions.length - 1].date)?.format(
      `MM${t("normal.month")}DD${t("normal.date")}`
    )}`;
  };

  const getRefundOptions = (memberCount: number) => {
    let options: SelectProps["options"] = [
      { label: t(`refund.count.norefund`), value: 0 },
    ];
    options = options.concat(
      Array.from({ length: memberCount - 1 }).map((_, index) => {
        return {
          label: t(`refund.count.partial`, { count: index + 1 }),
          value: index + 1,
        };
      })
    );
    options = options.concat({ label: t(`refund.count.refundall`), value: -1 });
    return options;
  };

  const getFeePercent = (input: number): number | null => {
    const policies = refundPolicy.policy.cancellation_policy;
    for (const policy of policies) {
      const { min, max } = policy.days_range;

      if (min !== null && max === null && input >= min) {
        return policy.fee_percent;
      }

      if (min !== null && max !== null && input >= min && input <= max) {
        return policy.fee_percent;
      }
    }

    return null;
  };

  const calculateRefundAmount = (
    _order: SearchResponse,
    index: number,
    refund_count: number
  ) => {
    const service_fee = 1 - refundPolicy?.policy.service_fee_percent / 100;
    switch (refund_count) {
      case refundCount.REFUND_ALL: {
        //CALCULATE REFUND ALL HERE
        const cancel_amount = _order.total_price;
        const localToday = toLocalTimezone(dayjs());
        const localStartDate = toLocalTimezone(_order.start_date);
        let ellapsedDays: number | undefined = undefined;
        if (localToday && localStartDate) {
          ellapsedDays = localStartDate.diff(localToday, "day");
          const fee_percent = 1 - (getFeePercent(ellapsedDays) || 0) / 100;
          const amount = cancel_amount * service_fee * fee_percent;
          return amount;
        }
        break;
      }
      default: {
        //CALCULATE REFUND PARTIAL
        const one_people_amount =
          _order.extra_charge / (_order.total_people - 1);
        const amount = one_people_amount * service_fee * refund_count;

        return amount;
      }
    }
  };

  const onOptionChange = (val: any, index: number, _order: SearchResponse) => {
    switch (val) {
      case refundCount.REFUND_ALL: {
        const amount = calculateRefundAmount(_order, index, val);
        setCalculatedAmount((prev: number[]) => [
          ...prev.slice(0, index),
          amount || 0,
          ...prev.slice(index + 1),
        ]);
        form.setFieldsValue({ [`refund_type${index}`]: 1 });

        break;
      }
      case refundCount.NO_REFUND:
        form.setFieldsValue({ [`refund_type${index}`]: 0 });
        setCalculatedAmount((prev: number[]) => [
          ...prev.slice(0, index),
          0,
          ...prev.slice(index + 1),
        ]);
        break;
      default: {
        const amount = calculateRefundAmount(_order, index, val);
        setCalculatedAmount((prev: number[]) => [
          ...prev.slice(0, index),
          amount || 0,
          ...prev.slice(index + 1),
        ]);
        form.setFieldsValue({ [`refund_type${index}`]: 2 });
        break;
      }
    }
  };

  const renderOrderDetail = () => {
    if (!orderDetail) return;
    return orderDetail?.order_details?.map(
      (_order: SearchResponse, index: number) => {
        const image =
          _order?.session_details && _order?.session_details.length > 0
            ? _order.session_details[0].session_img
            : undefined;
        const totalMember = _order.adults + _order.kids;
        return (
          <div
            className={styles.orderItemContainer}
            key={`refund_item_${index}`}
          >
            <Flex style={{ width: "100%" }} vertical gap={16}>
              <Flex gap={20}>
                <Image
                  src={image}
                  preview={false}
                  width={84}
                  height={84}
                  style={{ objectFit: "cover", borderRadius: 8 }}
                />
                <Flex
                  align="flex-start"
                  justify="flex-start"
                  flex={1}
                  vertical
                  gap={4}
                >
                  <P1 weight="medium" color="var(--text-content-1">
                    {t(`skiresort.${_order.ski_resort_code}`)}
                  </P1>
                  <P1 color="var(--text-content-1">
                    {renderDate(_order.session_details || [])}
                  </P1>
                  <P1 color="var(--text-content-1)">
                    {t("refund.totalPlayer", { count: totalMember })}
                  </P1>
                  <Flex className={styles.totalPrice} gap={20} align="flex-end">
                    <P2 color="var(--text-content-2)">
                      {t("record.subtotal")}
                    </P2>
                    <P2 color="var(--text-content-2)" weight="medium">
                      {`${t(
                        `currency.${process.env.REACT_APP_CURRENCY}`
                      )} ${moneyFormat(_order.total_price)}`}
                    </P2>
                  </Flex>
                </Flex>
              </Flex>
              <Form.Item
                name={`order_number${index}`}
                hidden
                initialValue={_order.order_number}
              />
              <Form.Item name={`refund_type${index}`} hidden />
              <Form.Item
                label={t("refund.playerCount.label")}
                name={`refund_person_count${index}`}
                rules={[InputRequired(t, "refund.playerCount.label")]}
              >
                <Select
                  placeholder={t("refund.hint")}
                  // style={{ width: 100 }}
                  options={getRefundOptions(totalMember)}
                  // open={openSelect}
                  // onClick={handleClickAreaCode}
                  onChange={(val) => onOptionChange(val, index, _order)}
                />
              </Form.Item>
            </Flex>
          </div>
        );
      }
    );
  };

  const cardsEnum: CardsEnumType[] = [
    {
      title: t(`refund.introduction.title`),
      content: (
        <div className={styles.wrapper}>
          <P2 color="var(--text-content-1)">
            <Trans i18nKey={"refund.content1"} />
          </P2>
          <Table
            style={{ width: "100%" }}
            bordered
            pagination={false}
            columns={tableColumn}
            dataSource={tableSource}
            className="custom-refund"
          />
          <P2 color="var(--text-content-1)">
            <Trans i18nKey={"refund.content2"} />
          </P2>
          <P2 className={styles.content} color="var(--text-content-1)">
            <Trans
              i18nKey={"refund.content3"}
              components={{
                span: <span />,
              }}
            />
          </P2>
        </div>
      ),
    },
    {
      title: t("recordDetail.orderInfo"),
      content: (
        <Row gutter={[0, 8]}>
          <Col span={screens.sm ? 12 : 24}>
            <Flex align="center" justify="flex-start" gap={29}>
              <P2 color="var(--text-content-3)">
                {t("recordDetail.orderPrice")}
              </P2>
              <P2 weight="medium" color="var(--text-content-1)">{`${t(
                `currency.${envChangeType("REACT_APP_CURRENCY")}`
              )} ${moneyFormat(queryParams?.amount || 0)}`}</P2>
            </Flex>
          </Col>
          <Col span={screens.sm ? 12 : 24}>
            <Flex align="center" justify="flex-start" gap={29}>
              <P2 color="var(--text-content-3)">{t("refund.paymentNumber")}</P2>
              <Flex gap={8} align="center">
                <P2
                  weight="medium"
                  color="var(--text-content-1)"
                >{`${queryParams?.payment_number}`}</P2>
                <Copy
                  style={{ color: "var(--primary)", cursor: "pointer" }}
                  onClick={() =>
                    copyText(
                      queryParams?.payment_number || "",
                      t("message.copy.success")
                    )
                  }
                />
              </Flex>
            </Flex>
          </Col>
        </Row>
      ),
    },
    {
      title: t("record.applyRefund"),
      content: (
        <Form
          style={{ width: "100%" }}
          autoComplete={"off"}
          onFinish={onFinish}
          form={form}
          requiredMark={false}
          layout={"vertical"}
        >
          {renderOrderDetail()}
          <Flex
            gap={16}
            align="center"
            justify="flex-start"
            style={{ marginBottom: 34 }}
          >
            <P2 color="var(--text-content-3">
              {t("refund.calculate.refundamount")}
            </P2>
            <P2 weight="heavy" color="var(--warn)">
              {`${t(
                `currency.${envChangeType("REACT_APP_CURRENCY")}`
              )} ${moneyFormat(totalRefundAmount)}`}
            </P2>
          </Flex>
          <Form.Item
            name="refund_reason"
            label={t("refund.reason.title")}
            rules={[InputRequired(t, "refund.reason.title")]}
          >
            <TextArea
              style={{ height: 130 }}
              placeholder={t("refund.reason.hint")}
              maxLength={100}
              showCount
            />
          </Form.Item>
          <div
            className={`${styles.buttoncontainer} ${
              screens.sm ? "" : styles.mobile
            }`}
          >
            <Button
              block={screens.sm ? false : true}
              onClick={onCancel}
              loading={isPendingRefund}
            >
              {t("normal.cancel")}
            </Button>
            <Button
              loading={isPendingRefund}
              block={screens.sm ? false : true}
              type="primary"
              htmlType="submit"
            >
              {t("refund.submit")}
            </Button>
          </div>
        </Form>
      ),
    },
  ];

  return (
    <ContentWrapper>
      <div
        className={`${styles.recordDetail} recordDetail`}
        style={{ gap: screens.sm ? 24 : 16 }}
      >
        {cardsEnum.map((card: CardsEnumType, index: number) => {
          if (card.hide) return null;
          return (
            <Card
              key={index}
              style={{ width: "100%" }}
              size={screens.sm ? "default" : "small"}
              title={
                <P1 weight="medium" color=" var(--text-content-1)">
                  {card.title}
                </P1>
              }
              extra={card.extra}
              variant="borderless"
            >
              {card.content}
            </Card>
          );
        })}
      </div>
    </ContentWrapper>
  );
};

export default Refund;
